import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Mousetrap from 'mousetrap'
import {
  magicTableShortcuts,
  navigationShortcuts
} from '../../components/KeyboardShortcutsOverlay/shortcuts'
import { useNavigationV3Context } from '../../components/NavigationV3/hooks/useNavigationV3Context'

// Make sure Mousetrap doesn't get stuck waiting for sequences
Mousetrap.stopCallback = function (e, element) {
  // if the element has the class "mousetrap" then no need to stop
  if ((' ' + element.className + ' ').indexOf(' mousetrap ') > -1) {
    return false
  }
  // stop for input, select, and textarea
  return (
    element.tagName === 'INPUT' ||
    element.tagName === 'SELECT' ||
    element.tagName === 'TEXTAREA' ||
    element.getAttribute('contenteditable') === 'true'
  )
}

const SEQUENCE_TIMEOUT_MS = 1000
// When a complete shortcut has a longer extension (e.g. `g w` vs
// `g w w`), defer the shorter action briefly so an in-flight third
// key can match instead. Long enough to be reachable, short enough to
// keep the common 2-key case feeling instant.
const PREFIX_CONFLICT_DELAY_MS = 350

export const useKeyboardNavigation = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { data, functions } = useNavigationV3Context()
  const { isNavigationCollapsed } = data
  const { toggleNavigationCollapsed } = functions

  useEffect(() => {
    if (!navigate) return

    const navigateRoute = (route: string) => {
      try {
        if (location.pathname !== route.split('?')[0]) {
          navigate(route)
        }
      } catch (error) {
        console.error('Navigation error:', error)
      }
    }

    const clickShortcutTarget = (target: string) => {
      const button = document.querySelector<HTMLElement>(
        `[data-keyboard-shortcut-target="${target}"]`
      )
      button?.click()
    }

    type Shortcut = { keys: string[]; run: () => void }
    const shortcuts: Shortcut[] = [
      ...navigationShortcuts
        .filter(s => s.route)
        .map(s => ({ keys: s.keys, run: () => navigateRoute(s.route!) })),
      ...magicTableShortcuts.map(s => ({
        keys: s.keys,
        run: () => clickShortcutTarget(s.target)
      }))
    ]

    // Set of every key used in any sequence position so we know which
    // single-key bindings to register with Mousetrap.
    const allKeys = Array.from(new Set(shortcuts.flatMap(s => s.keys)))

    const sequencesEqual = (a: string[], b: string[]) =>
      a.length === b.length && a.every((k, i) => k === b[i])
    const sequenceStartsWith = (full: string[], prefix: string[]) =>
      prefix.length <= full.length && prefix.every((k, i) => full[i] === k)

    let current: string[] = []
    let sequenceTimer: NodeJS.Timeout | null = null
    let pendingMatch: Shortcut | null = null
    let pendingTimer: NodeJS.Timeout | null = null

    const reset = () => {
      current = []
      pendingMatch = null
      if (sequenceTimer) {
        clearTimeout(sequenceTimer)
        sequenceTimer = null
      }
      if (pendingTimer) {
        clearTimeout(pendingTimer)
        pendingTimer = null
      }
    }

    const firePending = () => {
      const match = pendingMatch
      pendingMatch = null
      if (pendingTimer) {
        clearTimeout(pendingTimer)
        pendingTimer = null
      }
      reset()
      match?.run()
    }

    const handleKey = (key: string) => {
      if (sequenceTimer) {
        clearTimeout(sequenceTimer)
        sequenceTimer = null
      }
      // A pending shortcut is waiting on a possible longer match. If
      // the new key continues that path, keep going; otherwise commit
      // the pending action and start a fresh sequence with the new key.
      if (pendingMatch) {
        const candidate = [...current, key]
        const stillExtendable = shortcuts.some(
          s =>
            !sequencesEqual(s.keys, current) &&
            sequenceStartsWith(s.keys, candidate)
        )
        const completesLonger = shortcuts.some(
          s =>
            !sequencesEqual(s.keys, current) &&
            sequencesEqual(s.keys, candidate)
        )
        if (completesLonger || stillExtendable) {
          if (pendingTimer) clearTimeout(pendingTimer)
          pendingTimer = null
          pendingMatch = null
          // Fall through to the normal handler with the extended sequence.
          current = candidate
          return resolveCurrent()
        }
        firePending()
        // Reset done; new key starts a fresh sequence below.
      }

      current = [...current, key]
      return resolveCurrent()
    }

    const resolveCurrent = (): boolean => {
      const exact = shortcuts.find(s => sequencesEqual(s.keys, current))
      const hasExtensions = shortcuts.some(
        s =>
          !sequencesEqual(s.keys, current) &&
          sequenceStartsWith(s.keys, current)
      )
      const isPrefix = shortcuts.some(s => sequenceStartsWith(s.keys, current))

      if (exact && hasExtensions) {
        pendingMatch = exact
        pendingTimer = setTimeout(firePending, PREFIX_CONFLICT_DELAY_MS)
        return false
      }
      if (exact) {
        const match = exact
        reset()
        match.run()
        return false
      }
      if (isPrefix) {
        sequenceTimer = setTimeout(reset, SEQUENCE_TIMEOUT_MS)
        return false
      }
      // Dead end: drop the sequence. The current key may itself start
      // a new sequence, so re-resolve from scratch with just this key.
      const lastKey = current[current.length - 1]
      reset()
      if (shortcuts.some(s => sequenceStartsWith(s.keys, [lastKey]))) {
        current = [lastKey]
        sequenceTimer = setTimeout(reset, SEQUENCE_TIMEOUT_MS)
        return false
      }
      return true
    }

    Mousetrap.bind('[', () => {
      if (isNavigationCollapsed) return true
      toggleNavigationCollapsed()
      return false
    })

    Mousetrap.bind(']', () => {
      if (!isNavigationCollapsed) return true
      toggleNavigationCollapsed()
      return false
    })

    allKeys.forEach(key => {
      Mousetrap.bind(key, () => handleKey(key))
    })

    return () => {
      reset()
      Mousetrap.unbind('[')
      Mousetrap.unbind(']')
      allKeys.forEach(key => {
        Mousetrap.unbind(key)
      })
    }
  }, [
    navigate,
    location.pathname,
    isNavigationCollapsed,
    toggleNavigationCollapsed
  ])

  return navigationShortcuts
}
